/*
 * Copyright (c) 2023 by N-Ach (Pvt) Ltd
 *
 * ╔═╗ ╔╗    ╔═══╗  ╔╗
 * ║║╚╗║║    ║╔═╗║  ║║
 * ║╔╗╚╝║    ║║ ║╠══╣╚═╗
 * ║║╚╗║║╔══╗║╚═╝║╔═╣╔╗║
 * ║║ ║║║╚══╝║╔═╗║╚═╣║║║
 * ╚╝ ╚═╝    ╚╝ ╚╩══╩╝╚╝
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information
 * of N-Ach (Pvt) Ltd. You shall not disclose such Confidential
 * Information and shall use it only in accordance with the terms
 * of the license agreement you entered into with N-Ach (Pvt) Ltd.
 *
 *
 * @Author Dilhari Jayathunga
 * @Date 16/11/2023
 */

import {
  Backdrop,
  CircularProgress,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import styles from "./Summary.module.css";
import React, { useContext } from "react";
import CheckoutContext from "../../Context/CheckoutContext";
import { isMobile } from "react-device-detect";
import { useLazyGetPriceByCatHrsResIdsQuery } from "../../Store/Services/PriceService";
import { useEffect } from "react";
import { SET_TOTAL } from "../../Pages/CheckoutPage";
import { useSelector } from "react-redux";

const Summary = () => {
  const { checkoutData, dispatch } = useContext(CheckoutContext);
  const savedAddress = useSelector((state) => state.address.savedAddress);

  const [getPrice, { data, isSuccess, isLoading }] =
    useLazyGetPriceByCatHrsResIdsQuery();

  useEffect(() => {
    const getData = async () => {
      await getPrice({
        catId: checkoutData.catId,
        noRes: checkoutData.noRes,
        noHrs: checkoutData.noHrs,
      }).unwrap();
    };
    getData();
  }, [checkoutData]);

  useEffect(() => {
    if (isSuccess && !isLoading) {
      Object.keys(data).length !== 0
        ? dispatch({ type: SET_TOTAL, payload: data.fullPayment })
        : dispatch({ type: SET_TOTAL, payload: 0 });
    }
  }, [data]);

  return (
    <>
      {isLoading && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      {!isMobile && (
        <Paper className={styles.paper} elevation={isMobile ? 0 : 3}>
          <Typography className={styles.topic}>Details</Typography>
          <div className={styles.inputContainer}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography className={styles.inputLabel}>Service</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography className={styles.inputLabel}>
                  {checkoutData.subCatName}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography className={styles.inputLabel}>Duration</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography className={styles.inputLabel}>
                  {checkoutData.noHrs} hrs
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography className={styles.inputLabel}>
                  No of Professionals
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography className={styles.inputLabel}>
                  {checkoutData.noRes}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography className={styles.inputLabel}>Materials</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography className={styles.inputLabel}>
                  {checkoutData.isMaterialsNeeded == false ? "No" : "Yes"}
                </Typography>
              </Grid>
              {checkoutData.date && checkoutData.timeSlot && (
                <Grid item xs={6}>
                  <Typography className={styles.inputLabel}>
                    Date & Start Time
                  </Typography>
                </Grid>
              )}
              {checkoutData.date && checkoutData.timeSlot && (
                <Grid item xs={6}>
                  <Typography className={styles.inputLabel}>
                    {checkoutData.date},<br /> {checkoutData.timeSlot}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={6}>
                <Grid item xs={6}>
                  {checkoutData?.stateName || savedAddress ? (
                    <Typography className={styles.inputLabel}>
                      Address
                    </Typography>
                  ) : null}
                </Grid>{" "}
              </Grid>
              <Grid item xs={6}>
                {checkoutData?.stateName || savedAddress ? (
                  <Grid item xs={6}>
                    <Typography className={styles.inputLabel}>
                      {checkoutData?.stateName ? (
                        <>
                          {checkoutData?.addressLine3}
                          {checkoutData?.addressLine3 && ","}{" "}
                          {checkoutData?.addressLine2}
                          {checkoutData?.addressLine2 && ","}{" "}
                          {checkoutData?.addressLine1}, {checkoutData?.areaName}
                          , {checkoutData?.stateName}
                        </>
                      ) : (
                        <>
                          {savedAddress?.addLine3}
                          {savedAddress?.addLine3 && ","}{" "}
                          {savedAddress?.addLine2}
                          {savedAddress?.addLine2 && ","}{" "}
                          {savedAddress?.addLine1}, {savedAddress?.areaName},{" "}
                          {savedAddress?.stateName}
                        </>
                      )}
                    </Typography>
                  </Grid>
                ) : null}
              </Grid>
            </Grid>
          </div>
        </Paper>
      )}
      {!isMobile && (
        <Paper
          className={styles.paper}
          style={{ marginTop: "1.8rem" }}
          elevation={isMobile ? 0 : 3}
        >
          <Typography className={styles.topic}>Payment Summary</Typography>
          <div className={styles.inputContainer}>
            {isSuccess && (
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  {Object.keys(data).length !== 0 ? (
                    <Typography className={styles.inputLabel}>
                      Subtotal{" "}
                      {data.offer !== 0 ? ` (with ${data.offer}% offer)` : null}
                    </Typography>
                  ) : (
                    <Typography className={styles.inputLabel}>
                      Subtotal
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={6}>
                  {Object.keys(data).length !== 0 ? (
                    <Typography align="right" className={styles.inputLabel}>
                      {process.env.REACT_APP_CURRENCY}{" "}
                      {data.totwithOff
                        ? data.totwithOff.toFixed(2)
                        : checkoutData.amount.toFixed(2)}{" "}
                      {data.offer !== 0 ? (
                        <span
                          style={{
                            textDecoration: "line-through",
                            color: "#a90000",
                          }}
                        >
                          {" "}
                          {process.env.REACT_APP_CURRENCY}{" "}
                          {data.totalPrice.toFixed(2)}
                        </span>
                      ) : null}
                    </Typography>
                  ) : (
                    <Typography align="right" className={styles.inputLabel}>
                      {process.env.REACT_APP_CURRENCY} 0
                    </Typography>
                  )}
                </Grid>
              </Grid>
            )}
            {isSuccess
              ? data?.charges?.map((charge, index) => (
                <Grid container spacing={2} key={`${charge.chargeId}-${index}`}>
                  <Grid item xs={6} marginTop={2}>
                    <Typography className={styles.inputLabel}>
                      {charge.description}{" "}
                      {charge.chargePercentage !== 0 
                      ? `(${charge.chargePercentage}%)` 
                      : null}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} marginTop={2}>
                    <Typography align="right" className={styles.inputLabel}>
                      {process.env.REACT_APP_CURRENCY}{" "} 
                      {charge.finalAmount.toFixed(2)}
                    </Typography>
                  </Grid>
                </Grid>
              ))
              : null}

            <hr />
            {isSuccess && (
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  {Object.keys(data).length !== 0 ? (
                    <Typography className={styles.subTitle}>Total</Typography>
                  ) : (
                    <Typography className={styles.subTitle}>Total</Typography>
                  )}
                </Grid>
                <Grid item xs={6}>
                  {Object.keys(data).length !== 0 ? (
                    <Typography align="right" className={styles.subTitle}>
                      {process.env.REACT_APP_CURRENCY}{" "}
                      {data.fullPayment.toFixed(2)}
                    </Typography>
                  ) : (
                    <Typography align="right" className={styles.subTitle}>
                      {process.env.REACT_APP_CURRENCY} 0.00
                    </Typography>
                  )}
                </Grid>
              </Grid>
            )}
          </div>
        </Paper>
      )}
    </>
  );
};

export default Summary;
